// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import DatePicker from "@insite/mobius/DatePicker";
import DynamicDropdown from "@insite/mobius/DynamicDropdown";
import { FormFieldStyle } from "@insite/mobius/FormField";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Search from "@insite/mobius/Icons/Search";
import Link from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { DLSectionTitle, SectionContainer } from "@werner/App/Widgets/DesignSystem/DesignSystem.helpers";
import { dynamicDropdownOptions, longText } from "@werner/App/Widgets/DesignSystem/DummyData/DummyData";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface FormTabStyles {
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    formWrapper?: InjectableCss;
    columnFormWrapper?: InjectableCss;
}

const formTabStyles: FormTabStyles = {
    gridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-top: 20px;
            flex-wrap: wrap;
            ${FormFieldStyle} {
                max-width: 250px;
            }
        `,
    },
    columnFormWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            margin-top: 20px;
            ${FormFieldStyle} {
                max-width: 250px;
            }
        `,
    },
};

const styles = formTabStyles;
const FormTab: FC = () => {
    const [radioValue, setRadioValue] = useState("All");
    const onRadioChangeHandler = (e: any) => {
        setRadioValue(e.target.value);
    };

    return (
        <SectionContainer>
            <GridItem {...styles.gridItem}>
                <DLSectionTitle>TextField</DLSectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <TextField placeholder="test" label="User Name - Required" required data-test-selector="userName" />
                    <TextField placeholder="test" label="User Name - Disabled" disabled data-test-selector="userName" />
                    <TextField
                        placeholder="test"
                        label="User Name - Error"
                        error="Error"
                        data-test-selector="userName"
                    />
                    <TextField
                        label="With Icon"
                        data-test-selector="withIcon"
                        iconProps={{
                            src: Search,
                        }}
                        iconClickableProps={{
                            type: "button",
                        }}
                        placeholder="test"
                    />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <DLSectionTitle>TextArea</DLSectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <TextArea placeholder="test" required label="Description - Required" />
                    <TextArea placeholder="test" defaultValue={longText} label="Description - Long Text" />
                    <TextArea placeholder="test" defaultValue={longText} disabled label="Description - Disabled" />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <DLSectionTitle>Select</DLSectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <Select label="Select the best one" required placeholder="Select" onChange={event => {}}>
                        <option value="Day">{translate("Day")}</option>
                        <option value="Month">{translate("Month")}</option>
                    </Select>
                    <Select label="Select the best one" disabled placeholder="Select" onChange={event => {}}>
                        <option value="Day">{translate("Day")}</option>
                        <option value="Month">{translate("Month")}</option>
                    </Select>
                    <Select label="Pagination" required placeholder="Pagination" onChange={event => {}}>
                        <option value={8}>8</option>
                        <option value={16}>16</option>
                        <option value={32}>32</option>
                    </Select>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <DLSectionTitle>DatePicker</DLSectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <DatePicker label="Date" />
                    <DatePicker label="Date (required)" required />
                    <DatePicker label="Date (selected)" selectedDay={new Date(2019, 8, 12)} />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <DLSectionTitle>Checkbox</DLSectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <CheckboxGroup label="Checkbox Group Label">
                        <Checkbox checked>Checked</Checkbox>
                        <Checkbox>Unchecked</Checkbox>
                        <Checkbox checked disabled>
                            Disabled Checked
                        </Checkbox>
                        <Checkbox checked="indeterminate" disabled>
                            Disabled Indeterminate
                        </Checkbox>
                        <Checkbox disabled>Disabled</Checkbox>
                        <Checkbox variant="toggle">Toggle</Checkbox>
                        <Checkbox disabled variant="toggle">
                            Toggle Disabled
                        </Checkbox>
                    </CheckboxGroup>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <DLSectionTitle>Radio</DLSectionTitle>
                <StyledWrapper {...styles.columnFormWrapper}>
                    <RadioGroup value={radioValue} onChangeHandler={onRadioChangeHandler}>
                        <Radio key="All">All</Radio>
                        <Radio key="Unread">Unread</Radio>
                        <Radio key="Read">Read</Radio>
                        <Radio key="Disabled" disabled={true}>
                            Disabled
                        </Radio>
                    </RadioGroup>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <DLSectionTitle>Dynamic Dropdown</DLSectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <DynamicDropdown
                        options={dynamicDropdownOptions}
                        label="Dynamic Dropdown"
                        placeholder="test"
                        onSelectionChange={(value?: string) => {}}
                    />
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <DLSectionTitle>Links</DLSectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <Link color="secondary" href="https://www.google.com/">
                        Google
                    </Link>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <DLSectionTitle>Ordered List</DLSectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <OrderedList>
                        <ListItem>
                            Item 1
                            <OrderedList>
                                <ListItem>Item 1.1</ListItem>
                                <ListItem>Item 1.2</ListItem>
                                <ListItem>Item 1.3</ListItem>
                            </OrderedList>
                        </ListItem>
                        <ListItem>Item 2</ListItem>
                        <ListItem>Item 3</ListItem>
                        <ListItem>Item 4</ListItem>
                    </OrderedList>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <DLSectionTitle>Unordered List</DLSectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <UnorderedList>
                        <ListItem>
                            Item 1
                            <UnorderedList>
                                <ListItem>Item 1.1</ListItem>
                                <ListItem>Item 1.2</ListItem>
                                <ListItem>Item 1.3</ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>Item 2</ListItem>
                        <ListItem>Item 3</ListItem>
                        <ListItem>Item 4</ListItem>
                    </UnorderedList>
                </StyledWrapper>
            </GridItem>
        </SectionContainer>
    );
};

export default FormTab;
